import { cloneElement, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Input, NumericTextBox, TextArea, Checkbox } from "@progress/kendo-react-inputs";
import CleanDatePicker from "src/dm/components/CleanDatePicker/CleanDatePicker";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { Error as KError } from "@progress/kendo-react-labels";
import { useCreateDeal, useUpdateDeal } from "src/dm/hooks/useDeals";
import { apiClient } from "src/dm/utils/axios";
import {
  useDealAPVendors,
  useReplaceDealAPVendors,
} from "src/dm/hooks/useDealAPVendors";
import {
  useAdLocations,
  useAdSizes,
  useApVendors,
  useDealOwnersList,
  useDisplayPlans,
  useDistributionTypes,
  useFeatures,
  usePromoCodes,
  NumericLookupRow,
  StringLookupRow,
  DealOwnerListRow,
} from "src/dm/hooks/useLookups";
import UserName from "src/dm/components/UserName/UserName";
import {
  DealDetail,
  DealWriteRequest,
} from "src/dm/interfaces/Deal";
import * as s from "./DealForm.css";

type Mode = { kind: "create" } | { kind: "edit"; dealId: number };

interface DealFormProps {
  mode: Mode;
  initial?: DealDetail;
}

const emptyRequest = (): DealWriteRequest => ({
  description: "",
  bannerCode: "",
  categoryManagerCode: null,
  dealTypeId: null,
  isDemo: false,
  isInternalDeal: false,
  isFloorStockProtected: false,
  communityMarketExcluded: false,
  hasOffInvoiceAllowance: false,
  hasBillBackAllowance: false,
  hasCountRecountAllowance: false,
  hasPassThruAllowance: false,
  hasScanbackAllowance: false,
  allowanceReflectsToSixtyOne: false,
  isRewardPromotion: false,
  isSecondaryPromotion: false,
  sendExpirationNotification: false,
  isGeac: false,
  isTermSheet: false,
  suppressSavePriceFormat: false,
  wineSaveUpTo: false,
  excludedFromAmp: false,
  // Promo-details dropdowns (audit 21 MAJOR #4). null = unselected.
  adSizeCode: null,
  adLocationId: null,
  adDistributionId: null,
  displayPlanId: null,
  promotionCodeId: null,
  adFeatureId: null,
  apVendorNumbers: [],
  // Audit 26 §3 gap-closure item 4 — vendor contact + ad-block price format +
  // manual-entry stamp. Defaults to null/empty for a fresh New Deal.
  contactName: null,
  contactPhone: null,
  contactEmail: null,
  adBlockPriceFormatCode: null,
  datePrintedForManualEntry: null,
});

const fromDetail = (d: DealDetail): DealWriteRequest => ({
  description: d.description ?? "",
  bannerCode: d.bannerCode ?? "",
  categoryManagerCode: d.categoryManagerCode,
  dealTypeId: d.dealTypeId,
  adStartDate: d.adStartDate,
  adEndDate: d.adEndDate,
  promoStartDate: d.promoStartDate,
  promoEndDate: d.promoEndDate,
  scanbackStartDate: d.scanbackStartDate,
  scanbackEndDate: d.scanbackEndDate,
  reflectStartDate: d.reflectStartDate,
  reflectEndDate: d.reflectEndDate,
  countRecountStartDate: d.countRecountStartDate,
  countRecountEndDate: d.countRecountEndDate,
  firstOrderDate: d.firstOrderDate,
  lastOrderDate: d.lastOrderDate,
  firstShipmentDate: d.firstShipmentDate,
  lastShipmentDate: d.lastShipmentDate,
  bestHistoryDate: d.bestHistoryDate,
  signageEndDate: d.signageEndDate,
  // Reward promo dates — round-tripped only when the Reward Promotion flag
  // is checked. Audit 23 §1: legacy NewDeal.aspx writes these from
  // txtRewardStart / txtRewardEnd; the SPA form previously had no field at
  // all so the column always stayed null.
  rewardPromoStartDate: d.rewardPromotionStartDate,
  rewardPromoEndDate: d.rewardPromotionEndDate,
  promoMaxQty: d.promoMaxQty,
  minimumPurchaseQuantity: d.minimumPurchaseQuantity,
  maxStoreCountAllowedForWrap: d.maxStoreCountAllowedForWrap,
  projectedUnitSales: d.projectedUnitSales,
  projectedUnitsUom: d.projectedUnitsUom,
  dealCost: d.dealCost,
  advertisingFee: d.advertisingFee,
  administrationFee: d.administrationFee,
  flySheetFee: d.flySheetFee,
  otherCharges: d.otherCharges,
  ownerUserId: d.ownerUserId,
  vendorBrokerId: d.vendorBrokerId,
  adBlockId: d.adBlockId,
  adPlanCode: d.adPlanCode,
  adStrength: d.adStrength,
  adFeatureSequence: d.adFeatureSequence,
  countryOfOrigin: d.countryOfOrigin,
  thirdPartyNotification: d.thirdPartyNotification,
  consumerMessageComment: d.consumerMessageComment,
  couponComment: d.couponComment,
  ampStoreComment: d.ampStoreComment,
  ampMerchandisingComment: d.ampMerchandisingComment,
  ampAdvertisingComment: d.ampAdvertisingComment,
  signageNotes: d.signageNotes,
  isDemo: d.isDemo,
  isInternalDeal: d.isInternalDeal,
  isFloorStockProtected: d.isFloorStockProtected,
  communityMarketExcluded: d.communityMarketExcluded,
  hasOffInvoiceAllowance: d.hasOffInvoiceAllowance,
  hasBillBackAllowance: d.hasBillBackAllowance,
  hasCountRecountAllowance: d.hasCountRecountAllowance,
  hasPassThruAllowance: d.hasPassThruAllowance,
  hasScanbackAllowance: d.hasScanbackAllowance,
  allowanceReflectsToSixtyOne: d.allowanceReflectsToSixtyOne,
  isRewardPromotion: d.isRewardPromotion,
  isSecondaryPromotion: d.isSecondaryPromotion,
  sendExpirationNotification: d.sendExpirationNotification,
  isGeac: d.isGeac,
  isTermSheet: d.isTermSheet,
  suppressSavePriceFormat: d.suppressSavePriceFormat,
  wineSaveUpTo: d.wineSaveUpTo,
  excludedFromAmp: d.excludedFromAmp,
  // Promo-details dropdowns — server round-trips these on GET.
  adSizeCode: d.adSizeCode,
  adLocationId: d.adLocationId,
  adDistributionId: d.adDistributionId,
  displayPlanId: d.displayPlanId,
  promotionCodeId: d.promotionCodeId,
  adFeatureId: d.adFeatureId,
  apVendorNumbers: [],
  // Audit 26 §3 gap-closure item 4 — round-trip vendor contact + ad-block
  // price format + manual-entry print stamp so a Save doesn't blank them.
  contactName: d.contactName,
  contactPhone: d.contactPhone,
  contactEmail: d.contactEmail,
  adBlockPriceFormatCode: d.adBlockPriceFormatCode,
  datePrintedForManualEntry: d.datePrintedForManualEntry,
});

const toIso = (d: Date | null): string | null =>
  d ? new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString() : null;

const fromIso = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Sentinel "Select a …" entries for each DropDownList. Kendo's DropDownList
// only renders rows from `data`, so we prepend a null-id row to give the user
// a way back to "no selection". The legacy NewDeal.aspx used the same trick
// with value "-1" for the placeholder option.
const numericPlaceholder = (label: string): NumericLookupRow => ({
  id: -1,
  label,
});
const stringPlaceholder = (label: string): StringLookupRow => ({
  id: "",
  label,
});

// Floor Stock Protection is a tri-state in the legacy ddlflrStkPtn (blank /
// Yes / No). The backend column FSP_FG is still a Y/N flag but blank/null is
// observable in the database — represented here as "" so users can leave it
// unset on create.
type FspChoice = "" | "Y" | "N";
const FSP_OPTIONS: { id: FspChoice; label: string }[] = [
  { id: "", label: "(unset)" },
  { id: "Y", label: "Yes" },
  { id: "N", label: "No" },
];

const DealForm = ({ mode, initial }: DealFormProps) => {
  const navigate = useNavigate();
  const [form, setForm] = useState<DealWriteRequest>(() =>
    initial ? fromDetail(initial) : emptyRequest()
  );

  // Floor Stock Protection — owned locally so we can keep the tri-state UI
  // distinct from the boolean column on the wire. When initial detail loads
  // we map true→"Y", false→"N", and leave "" as a default-create option.
  const [fspChoice, setFspChoice] = useState<FspChoice>(() =>
    initial ? (initial.isFloorStockProtected ? "Y" : "N") : ""
  );

  // Hydrate AP vendor selections for edit mode from the existing child rows.
  const existingApVendors = useDealAPVendors(
    mode.kind === "edit" ? mode.dealId : undefined
  );

  useEffect(() => {
    if (initial) {
      setForm(fromDetail(initial));
      setFspChoice(initial.isFloorStockProtected ? "Y" : "N");
    }
  }, [initial]);

  useEffect(() => {
    if (existingApVendors.data && existingApVendors.data.length > 0) {
      setForm((prev) => ({
        ...prev,
        apVendorNumbers: existingApVendors.data!.map((v) => v.apVendorNumber),
      }));
    }
  }, [existingApVendors.data]);

  // Track the vendor/broker id we last auto-defaulted against, so a user who
  // *explicitly* clears the A/P Vendor selection doesn't have us silently put
  // it back on the next render. The auto-default fires once per distinct
  // vendor/broker value — matching legacy NewDeal.aspx `UpdateCatMgr('vendor')`
  // which only ran on the change event.
  const [autoDefaultedForVendor, setAutoDefaultedForVendor] = useState<number | null>(null);

  const create = useCreateDeal();
  const update = useUpdateDeal(mode.kind === "edit" ? mode.dealId : 0);
  // ap-vendor replacement is a separate child endpoint (delete-then-insert).
  // For create we know the dealId only after the POST succeeds; we run the
  // mutation against the newly-created id then. For edit we run against the
  // form's mode.dealId.
  const replaceApVendorsExisting = useReplaceDealAPVendors(
    mode.kind === "edit" ? mode.dealId : 0
  );

  const mutating = mode.kind === "create" ? create : update;
  const errorMessage = useMemo(() => {
    const err = mutating.error;
    if (!err) return null;
    const anyErr = err as { response?: { data?: unknown }; message?: string };
    const data = anyErr.response?.data;
    if (data && typeof data === "object") return JSON.stringify(data, null, 2);
    return anyErr.message ?? "Unknown error";
  }, [mutating.error]);

  const setField = <K extends keyof DealWriteRequest>(key: K, value: DealWriteRequest[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // ---- Lookup data sources for the new dropdowns ------------------------
  const adSizes = useAdSizes();
  const adLocations = useAdLocations();
  const distributionTypes = useDistributionTypes();
  const displayPlans = useDisplayPlans();
  const features = useFeatures();
  // PromoCodes is banner-aware: re-fetch when the user picks a banner.
  const promoCodes = usePromoCodes(form.bannerCode || undefined);
  // AP vendors are vendor-aware. Disabled until the user picks a Vendor/Broker.
  const apVendorOptions = useApVendors(form.vendorBrokerId);
  // Deal-owners list — drives the Owner DropDownList (was previously a
  // free-text Input that required users to type DOMAIN\username exactly).
  // The API returns { userId, fullName } per row; until the RP_USER_PROFILE
  // join is gated on we render the raw userId via <UserName> so the dropdown
  // surfaces the friendly name when available and falls back to the raw id
  // (with the raw id in a tooltip via UserName's `title` attribute).
  const dealOwners = useDealOwnersList();
  const ownerData = useMemo<DealOwnerListRow[]>(
    () => [
      { userId: "", fullName: "— Unassigned —" },
      ...(dealOwners.data ?? []),
    ],
    [dealOwners.data]
  );
  // If the loaded deal has an owner that isn't in the distinct-owners list
  // (e.g. a user who has since left the team), the controlled DropDownList
  // would render with no selected row. Synthesise a stand-in so the value
  // round-trips on save without needing an extra fetch.
  const selectedOwnerRow = useMemo<DealOwnerListRow>(() => {
    const id = form.ownerUserId ?? "";
    if (!id) return ownerData[0];
    const match = ownerData.find((o) => o.userId === id);
    if (match) return match;
    return { userId: id, fullName: id };
  }, [form.ownerUserId, ownerData]);

  // Helpers to project Kendo DropDownList state from form ids. Kendo's
  // controlled-DropDownList wants the exact row object that lives in `data`,
  // not a primitive id — so we resolve it here and fall back to the sentinel
  // placeholder row when nothing is chosen.
  const adSizeData = useMemo(
    () => [stringPlaceholder("Select an Ad Size"), ...(adSizes.data ?? [])],
    [adSizes.data]
  );
  const adLocationData = useMemo(
    () => [numericPlaceholder("Select an Ad Location"), ...(adLocations.data ?? [])],
    [adLocations.data]
  );
  const distributionTypeData = useMemo(
    () => [numericPlaceholder("Select a Dist Method"), ...(distributionTypes.data ?? [])],
    [distributionTypes.data]
  );
  const displayPlanData = useMemo(
    () => [numericPlaceholder("Select a Display Plan"), ...(displayPlans.data ?? [])],
    [displayPlans.data]
  );
  const featureData = useMemo(
    () => [numericPlaceholder("Select a Feature"), ...(features.data ?? [])],
    [features.data]
  );
  // promo-codes hook returns { code: string, label: string } — normalise to
  // numeric ids the way DEAL.PROM_CD expects (NUMBER FK).
  const promoCodeData = useMemo(() => {
    const rows: NumericLookupRow[] = (promoCodes.data ?? []).map((row) => ({
      id: Number(row.code),
      label: row.label,
    }));
    return [numericPlaceholder("Select a Promotion Code"), ...rows];
  }, [promoCodes.data]);

  // ---- AP Vendor MultiSelect data + value -------------------------------
  // MultiSelect needs the same row-object treatment as DropDownList. The
  // returned hook already has { apVendorNumber, apVendorName }; we wrap into
  // a stable id/label shape for the kendo control.
  interface ApVendorPick {
    id: string;
    label: string;
  }
  const apVendorChoices: ApVendorPick[] = useMemo(
    () =>
      (apVendorOptions.data ?? []).map((v) => ({
        id: v.apVendorNumber,
        label: `${v.apVendorNumber} - ${v.apVendorName}`,
      })),
    [apVendorOptions.data]
  );
  // Show pills for the currently-selected ids even if the choices list hasn't
  // resolved yet (e.g. before the user picks a vendor on create).
  const selectedApVendorPicks: ApVendorPick[] = useMemo(() => {
    const selected = form.apVendorNumbers ?? [];
    return selected.map((id) => {
      const match = apVendorChoices.find((c) => c.id === id);
      return match ?? { id, label: id };
    });
  }, [form.apVendorNumbers, apVendorChoices]);

  // AP Vendor auto-select — audit 23-parity-deal-with-reward §4. Legacy
  // NewDeal.aspx's `UpdateCatMgr('vendor')` JS handler pre-selects the first
  // A/P vendor for the chosen Vendor/Broker once the lookup resolves. The
  // SPA previously left the MultiSelect empty, silently dropping the
  // DEAL_AP_VENDOR child row on every create. We replicate the behaviour
  // narrowly: when the user has not already chosen anything for the current
  // vendor/broker AND there is exactly one candidate AP vendor, auto-select
  // it. When there are multiple candidates we leave it empty (legacy picks
  // the first, but that's ambiguous — flag via `apVendorHint` so the SPA
  // can tell the user "pick one").
  useEffect(() => {
    const vid = form.vendorBrokerId;
    if (vid == null) return;
    if (autoDefaultedForVendor === vid) return; // already defaulted for this vendor
    if (!apVendorOptions.data) return; // wait for fetch
    if ((form.apVendorNumbers ?? []).length > 0) {
      // Already picked (edit-load or user) — mark "handled" so we don't fight.
      setAutoDefaultedForVendor(vid);
      return;
    }
    if (apVendorOptions.data.length === 1) {
      const only = apVendorOptions.data[0];
      setForm((prev) => ({ ...prev, apVendorNumbers: [only.apVendorNumber] }));
    }
    setAutoDefaultedForVendor(vid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.vendorBrokerId, apVendorOptions.data]);

  const apVendorHint = useMemo(() => {
    if (form.vendorBrokerId == null) return null;
    if (!apVendorOptions.data || apVendorOptions.data.length === 0) return null;
    if (apVendorOptions.data.length > 1 && (form.apVendorNumbers ?? []).length === 0) {
      return `Vendor has ${apVendorOptions.data.length} A/P vendors — pick at least one.`;
    }
    return null;
  }, [form.vendorBrokerId, apVendorOptions.data, form.apVendorNumbers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Project the tri-state Floor Stock Protection onto the boolean column —
    // unset is sent as `false` (legacy treated blank as "no" too; see
    // NewDeal.aspx.cs:771 where "0" or "-1" maps to false).
    const formToSend: DealWriteRequest = {
      ...form,
      isFloorStockProtected: fspChoice === "Y",
    };

    if (mode.kind === "create") {
      const result = await create.mutateAsync(formToSend);
      // Now that we have a dealId, push the AP-vendor list if any were chosen.
      // The child-table PUT replaces the entire set in one transaction.
      const chosen = formToSend.apVendorNumbers ?? [];
      if (chosen.length > 0) {
        // Run the child PUT inline against the newly-created dealId — we
        // don't have a hook bound to it (the hook factories take a fixed
        // dealId at construction time). Errors here are non-fatal: the deal
        // itself saved, and the user can re-apply A/P vendors from the edit
        // screen.
        try {
          await apiClient.put(`/dm/V1/deals/${result.dealId}/ap-vendors`, {
            apVendorNumbers: chosen,
          });
        } catch {
          // Swallow — see comment above.
        }
      }
      navigate(`/dm/deals/${result.dealId}`);
    } else {
      const result = await update.mutateAsync(formToSend);
      const chosen = formToSend.apVendorNumbers ?? [];
      try {
        await replaceApVendorsExisting.mutateAsync(chosen);
      } catch {
        // Same fallback semantics as create — surface via the error banner.
      }
      navigate(`/dm/deals/${result.dealId}`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={s.layout}>
      <h1 style={{ margin: 0 }}>
        {mode.kind === "create" ? "New Deal" : `Edit Deal ${mode.dealId}`}
      </h1>

      {errorMessage && (
        <pre className={s.errorBanner}>{errorMessage}</pre>
      )}

      <div className={s.sectionGrid}>
        <section className={s.card}>
          <h2 className={s.cardTitle}>Basics</h2>
          <label className={s.field}>
            <span className={s.fieldLabel}>Description *</span>
            <Input
              required
              maxLength={200}
              value={form.description}
              onChange={(e) => setField("description", String(e.target.value ?? ""))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Banner *</span>
            <Input
              required
              maxLength={20}
              value={form.bannerCode}
              onChange={(e) => setField("bannerCode", String(e.target.value ?? ""))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Category Manager Code *</span>
            <NumericTextBox
              value={form.categoryManagerCode ?? null}
              onChange={(e) => setField("categoryManagerCode", e.value ?? null)}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Deal Type ID *</span>
            <NumericTextBox
              value={form.dealTypeId ?? null}
              onChange={(e) => setField("dealTypeId", e.value ?? null)}
            />
          </label>
          <label className={s.field}>
            {/*
              Owner DropDownList (carry-forward SPA item 2). Replaces the
              previous free-text Input that required users to know the exact
              DOMAIN\username string. Sourced from /lookups/deal-owners-list
              (distinct OWNR_USER_ID join RP_USER_PROFILE). Items render via
              <UserName> so the friendly name appears when the WEBSEC2 join
              resolves, and the raw userId stays in a tooltip via UserName's
              `title` attribute. Filterable so users can type to find owners
              in long lists; the data set is small (~hundreds) so the filter
              runs client-side on Kendo's default `startswith` matcher.
            */}
            <span className={s.fieldLabel}>Owner</span>
            <DropDownList
              data={ownerData}
              textField="userId"
              dataItemKey="userId"
              loading={dealOwners.isLoading}
              filterable={true}
              value={selectedOwnerRow}
              itemRender={(li, itemProps) => {
                const row = itemProps.dataItem as DealOwnerListRow;
                // Clone the LI Kendo passes us so we keep its event handlers
                // (onClick selection / hover state) — replacing the children
                // with our custom render. Spreading `li.props` directly
                // fails TS because Kendo's element type carries extra DOM
                // properties (contentEditable enum widens to string) that
                // React's intrinsic LiHTMLAttributes rejects.
                const body = row.userId ? (
                  <UserName userId={row.userId} />
                ) : (
                  <span style={{ color: "#64748b" }}>{row.fullName}</span>
                );
                return cloneElement(li, li.props, body);
              }}
              onChange={(e) => {
                const row = e.target.value as DealOwnerListRow;
                setField("ownerUserId", row.userId || null);
              }}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Vendor / Broker ID</span>
            <NumericTextBox
              value={form.vendorBrokerId ?? null}
              onChange={(e) => setField("vendorBrokerId", e.value ?? null)}
            />
          </label>
          <label className={s.field}>
            {/*
              A/P Vendor (audit 21 MAJOR #4 item 1) — legacy ddcl-ddlAPVendor
              is a checkbox multi-select. Kendo MultiSelect on the SPA side.
              The list is loaded from /lookups/ap-vendors?vendorBrokerId=… so
              the user has to pick a Vendor/Broker first. The persisted set
              lives in DEAL_AP_VENDOR (PUT child endpoint replaces the whole
              collection on save).
            */}
            <span className={s.fieldLabel}>A/P Vendor (multi-select)</span>
            <MultiSelect
              data={apVendorChoices}
              textField="label"
              dataItemKey="id"
              value={selectedApVendorPicks}
              disabled={form.vendorBrokerId == null}
              placeholder={
                form.vendorBrokerId == null
                  ? "Pick a Vendor / Broker first"
                  : "Choose A/P vendor(s)"
              }
              onChange={(e) =>
                setField(
                  "apVendorNumbers",
                  (e.value as ApVendorPick[]).map((p) => p.id)
                )
              }
            />
            {apVendorHint && (
              <span style={{ color: "#a06a00", fontSize: "0.8rem" }}>{apVendorHint}</span>
            )}
          </label>
          {/*
            Vendor Contact trio (audit 26 §3 gap-closure item 4). Legacy
            NewDeal.aspx surfaces these as read-only labels populated from
            Vendor.VendorAttributes ("PrimaryContactName" / "Phone" /
            "PrimaryContactEmail" — see NewDeal.aspx.cs:162-173). The SPA
            promotes them to editable inputs so the captured value survives
            even if the vendor's attribute changes downstream. Persists to
            DEAL.CTCT_NM / CTCT_PHN / CTCT_EML.
          */}
          <label className={s.field}>
            <span className={s.fieldLabel}>Vendor Contact Name</span>
            <Input
              maxLength={120}
              value={form.contactName ?? ""}
              onChange={(e) => setField("contactName", String(e.target.value ?? "") || null)}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Vendor Contact Phone</span>
            <Input
              maxLength={40}
              value={form.contactPhone ?? ""}
              onChange={(e) => setField("contactPhone", String(e.target.value ?? "") || null)}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Vendor Contact Email</span>
            <Input
              type="email"
              maxLength={120}
              value={form.contactEmail ?? ""}
              onChange={(e) => setField("contactEmail", String(e.target.value ?? "") || null)}
            />
          </label>
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Ad &amp; Promotion Dates</h2>
          <label className={s.field}>
            <span className={s.fieldLabel}>Ad Start</span>
            <CleanDatePicker value={fromIso(form.adStartDate)} onChange={(e) => setField("adStartDate", toIso(e.value))} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Ad End</span>
            <CleanDatePicker value={fromIso(form.adEndDate)} onChange={(e) => setField("adEndDate", toIso(e.value))} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Promo Start</span>
            <CleanDatePicker value={fromIso(form.promoStartDate)} onChange={(e) => setField("promoStartDate", toIso(e.value))} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Promo End</span>
            <CleanDatePicker value={fromIso(form.promoEndDate)} onChange={(e) => setField("promoEndDate", toIso(e.value))} />
          </label>
          {/*
            Reward Promotion Start / End (audit 23-parity-deal-with-reward §1,
            re-gated per audit 26 §3 item 3). Mirrors legacy NewDeal.aspx where
            the txtRewardStart / txtRewardEnd controls live inside the
            `pnlReward` container that is only enabled when chkRewardPromo is
            checked. Per audit 26 we keep the inputs *visible* (so users can
            see what fields exist) but `disabled` until the flag is toggled —
            the flag's onChange clears the paired dates to match legacy
            behaviour. The columns are RWRD_PROM_STRT_DTE / RWRD_PROM_END_DTE
            on DEAL — the end column also participates in the EndDeal cascade.
          */}
          <label className={s.field}>
            <span className={s.fieldLabel}>Reward Promo Start</span>
            <CleanDatePicker
              disabled={!form.isRewardPromotion}
              value={fromIso(form.rewardPromoStartDate)}
              onChange={(e) => setField("rewardPromoStartDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Reward Promo End</span>
            <CleanDatePicker
              disabled={!form.isRewardPromotion}
              value={fromIso(form.rewardPromoEndDate)}
              onChange={(e) => setField("rewardPromoEndDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Best History</span>
            <CleanDatePicker value={fromIso(form.bestHistoryDate)} onChange={(e) => setField("bestHistoryDate", toIso(e.value))} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Signage End</span>
            <CleanDatePicker value={fromIso(form.signageEndDate)} onChange={(e) => setField("signageEndDate", toIso(e.value))} />
          </label>
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Allowance Dates</h2>
          {/*
            Allowance-flag gating (audit 26 §3 item 3). Legacy NewDeal.aspx
            enables the Scanback / CountRecount date inputs only when the
            corresponding allowance checkbox is ticked, and clears the date
            when it's unticked. We keep the inputs *visible* (so users see what
            fields are available) but `disabled` until the flag toggles on —
            the flag's onChange in the Flags card clears the paired dates to
            mirror legacy behaviour.
          */}
          <label className={s.field}>
            <span className={s.fieldLabel}>Scanback Start</span>
            <CleanDatePicker
              disabled={!form.hasScanbackAllowance}
              value={fromIso(form.scanbackStartDate)}
              onChange={(e) => setField("scanbackStartDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Scanback End</span>
            <CleanDatePicker
              disabled={!form.hasScanbackAllowance}
              value={fromIso(form.scanbackEndDate)}
              onChange={(e) => setField("scanbackEndDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Reflect Start</span>
            <CleanDatePicker value={fromIso(form.reflectStartDate)} onChange={(e) => setField("reflectStartDate", toIso(e.value))} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Reflect End</span>
            <CleanDatePicker value={fromIso(form.reflectEndDate)} onChange={(e) => setField("reflectEndDate", toIso(e.value))} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Count/Recount Start</span>
            <CleanDatePicker
              disabled={!form.hasCountRecountAllowance}
              value={fromIso(form.countRecountStartDate)}
              onChange={(e) => setField("countRecountStartDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Count/Recount End</span>
            <CleanDatePicker
              disabled={!form.hasCountRecountAllowance}
              value={fromIso(form.countRecountEndDate)}
              onChange={(e) => setField("countRecountEndDate", toIso(e.value))}
            />
          </label>
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Order &amp; Shipment</h2>
          {/*
            Allowance-flag gating (audit 26 §3 item 3). OffInvoice gates the
            Buy/Order First & Last dates; BillBack gates First/Last Shipment.
            Same visible-but-disabled treatment as the Scanback / CountRecount
            pairs above so the user can see which fields exist.
          */}
          <label className={s.field}>
            <span className={s.fieldLabel}>First Order</span>
            <CleanDatePicker
              disabled={!form.hasOffInvoiceAllowance}
              value={fromIso(form.firstOrderDate)}
              onChange={(e) => setField("firstOrderDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Last Order</span>
            <CleanDatePicker
              disabled={!form.hasOffInvoiceAllowance}
              value={fromIso(form.lastOrderDate)}
              onChange={(e) => setField("lastOrderDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>First Shipment</span>
            <CleanDatePicker
              disabled={!form.hasBillBackAllowance}
              value={fromIso(form.firstShipmentDate)}
              onChange={(e) => setField("firstShipmentDate", toIso(e.value))}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Last Shipment</span>
            <CleanDatePicker
              disabled={!form.hasBillBackAllowance}
              value={fromIso(form.lastShipmentDate)}
              onChange={(e) => setField("lastShipmentDate", toIso(e.value))}
            />
          </label>
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Quantities &amp; Cost</h2>
          <label className={s.field}>
            <span className={s.fieldLabel}>Projected Units</span>
            <NumericTextBox value={form.projectedUnitSales ?? null} onChange={(e) => setField("projectedUnitSales", e.value ?? null)} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>UOM</span>
            <Input value={form.projectedUnitsUom ?? ""} onChange={(e) => setField("projectedUnitsUom", String(e.target.value ?? "") || null)} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Min Vendor Purchase</span>
            <NumericTextBox value={form.minimumPurchaseQuantity ?? null} onChange={(e) => setField("minimumPurchaseQuantity", e.value ?? null)} />
          </label>
          <label className={s.field}>
            {/*
              Consumer Max Purchase Limit (audit 21 MAJOR #4 item 13) — legacy
              txtConsMaxPurLmt. Reuses the existing PROM_MAX_QTY column
              (`promoMaxQty`); see GiantEagle.Business.Deal.PromotionMaximumQuantityAllowed
              which is the same property. The legacy NewDeal form labelled
              this "Consumer Max Purchase Limit"; the new form had "Max Promo
              Qty" — same column, both labels acceptable. Renaming here to
              match the legacy form for parity.
            */}
            <span className={s.fieldLabel}>Consumer Max Purchase Limit</span>
            <NumericTextBox value={form.promoMaxQty ?? null} onChange={(e) => setField("promoMaxQty", e.value ?? null)} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Stores in Wrap</span>
            <NumericTextBox value={form.maxStoreCountAllowedForWrap ?? null} onChange={(e) => setField("maxStoreCountAllowedForWrap", e.value ?? null)} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Deal Cost</span>
            <NumericTextBox format="c2" value={form.dealCost ?? null} onChange={(e) => setField("dealCost", e.value ?? null)} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Other Charges</span>
            <NumericTextBox format="c2" value={form.otherCharges ?? null} onChange={(e) => setField("otherCharges", e.value ?? null)} />
          </label>
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Fees</h2>
          <label className={s.field}>
            {/*
              Performance Fee (audit 21 MAJOR #4 item 11) — legacy txtAdFee
              backed the DEAL.AD_FEE column, which the new schema continues to
              expose as `advertisingFee`. NewDeal.aspx labelled this field as
              "Performance Fee" (see runtime inventory line 207 and
              NewDeal.aspx.cs:957). Renaming here to match the legacy label.
            */}
            <span className={s.fieldLabel}>Performance Fee</span>
            <NumericTextBox format="c2" value={form.advertisingFee ?? null} onChange={(e) => setField("advertisingFee", e.value ?? null)} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Administration</span>
            <NumericTextBox format="c2" value={form.administrationFee ?? null} onChange={(e) => setField("administrationFee", e.value ?? null)} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Fly Sheet</span>
            <NumericTextBox format="c2" value={form.flySheetFee ?? null} onChange={(e) => setField("flySheetFee", e.value ?? null)} />
          </label>
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Ad Details</h2>
          {/*
            Promo-details dropdowns (audit 21 MAJOR #4 items 2-9, 14). All five
            DropDownList controls share the same shape — read the lookup, prepend
            a "Select a …" placeholder row, and project the form id onto the
            corresponding row object (Kendo's controlled DropDownList wants the
            row itself, not the primitive). Picking the placeholder back resets
            the form field to null so the column gets a NULL on save.
          */}
          <label className={s.field}>
            <span className={s.fieldLabel}>Ad Size</span>
            <DropDownList
              data={adSizeData}
              textField="label"
              dataItemKey="id"
              loading={adSizes.isLoading}
              value={adSizeData.find((o) => o.id === (form.adSizeCode ?? "")) ?? adSizeData[0]}
              onChange={(e) => {
                const row = e.target.value as StringLookupRow;
                setField("adSizeCode", row.id === "" ? null : row.id);
              }}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Ad Location</span>
            <DropDownList
              data={adLocationData}
              textField="label"
              dataItemKey="id"
              loading={adLocations.isLoading}
              value={adLocationData.find((o) => o.id === (form.adLocationId ?? -1)) ?? adLocationData[0]}
              onChange={(e) => {
                const row = e.target.value as NumericLookupRow;
                setField("adLocationId", row.id === -1 ? null : row.id);
              }}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Distribution Type</span>
            <DropDownList
              data={distributionTypeData}
              textField="label"
              dataItemKey="id"
              loading={distributionTypes.isLoading}
              value={distributionTypeData.find((o) => o.id === (form.adDistributionId ?? -1)) ?? distributionTypeData[0]}
              onChange={(e) => {
                const row = e.target.value as NumericLookupRow;
                setField("adDistributionId", row.id === -1 ? null : row.id);
              }}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Display Plan</span>
            <DropDownList
              data={displayPlanData}
              textField="label"
              dataItemKey="id"
              loading={displayPlans.isLoading}
              value={displayPlanData.find((o) => o.id === (form.displayPlanId ?? -1)) ?? displayPlanData[0]}
              onChange={(e) => {
                const row = e.target.value as NumericLookupRow;
                setField("displayPlanId", row.id === -1 ? null : row.id);
              }}
            />
          </label>
          <label className={s.field}>
            {/*
              Display Deal on Ad Plan (audit 21 MAJOR #4 item 7) — legacy
              ddlDispDealonAdPlan. Tied to DEAL.AD_PLAN_CD with three legal
              values ('P' / 'N' / 'R' = PrintAll / ExcludeAll /
              ExcludeRetailsAndCosts; see NewDeal.aspx.cs:1024-1038).
            */}
            <span className={s.fieldLabel}>Display Deal on Ad Plan</span>
            <DropDownList
              data={[
                { id: "P", label: "Print All" },
                { id: "N", label: "Exclude All" },
                { id: "R", label: "Exclude Retails and Costs" },
              ]}
              textField="label"
              dataItemKey="id"
              value={(() => {
                const opts = [
                  { id: "P", label: "Print All" },
                  { id: "N", label: "Exclude All" },
                  { id: "R", label: "Exclude Retails and Costs" },
                ];
                return opts.find((o) => o.id === form.adPlanCode) ?? opts[0];
              })()}
              onChange={(e) => setField("adPlanCode", (e.target.value as { id: string }).id)}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Ad Plan Code (raw)</span>
            <Input value={form.adPlanCode ?? ""} onChange={(e) => setField("adPlanCode", String(e.target.value ?? "") || null)} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Ad Strength</span>
            <Input value={form.adStrength ?? ""} onChange={(e) => setField("adStrength", String(e.target.value ?? "") || null)} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Ad Block ID</span>
            <NumericTextBox value={form.adBlockId ?? null} onChange={(e) => setField("adBlockId", e.value ?? null)} />
          </label>
          <label className={s.field}>
            {/*
              Ad Block Price Format (audit 26 §3 gap-closure item 4). Legacy
              ddlAdBlkPrcFmt (NewDeal.aspx:436, bound at NewDeal.aspx.cs:387-392
              from PriceFormat.GetAll(), persisted at NewDeal.aspx.cs:1014-1017).
              Free-text for now — when a /lookups/price-formats endpoint lands
              we'll swap to a DropDownList with the same row-shape treatment as
              the other promo-details lookups above. Persists to
              DEAL.AD_BLK_PRC_FMT_CD; null = no selection.
            */}
            <span className={s.fieldLabel}>Ad Block Price Format</span>
            <Input
              maxLength={10}
              value={form.adBlockPriceFormatCode ?? ""}
              onChange={(e) =>
                setField(
                  "adBlockPriceFormatCode",
                  String(e.target.value ?? "") || null
                )
              }
            />
          </label>
          <label className={s.field}>
            {/*
              Printed for Manual Entry (audit 26 §3 gap-closure item 4). Legacy
              `lblPrintedforManEntry` (NewDeal.aspx.cs:1350) shows the date
              read-only — the actual write happens via the dashboard
              "Print Summary for Manual Entry" bulk action
              (Default.aspx.cs:519-521 → DashboardController.cs:189-193). We
              surface the value here read-only so users know whether the deal
              has already been printed, and round-trip the field on PUT so a
              normal save doesn't blank the stamp.
            */}
            <span className={s.fieldLabel}>Printed for Manual Entry</span>
            <Input
              readOnly
              disabled
              value={
                form.datePrintedForManualEntry
                  ? new Date(form.datePrintedForManualEntry).toLocaleDateString()
                  : "(not yet printed)"
              }
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Promotion Code</span>
            <DropDownList
              data={promoCodeData}
              textField="label"
              dataItemKey="id"
              loading={promoCodes.isLoading}
              value={promoCodeData.find((o) => o.id === (form.promotionCodeId ?? -1)) ?? promoCodeData[0]}
              onChange={(e) => {
                const row = e.target.value as NumericLookupRow;
                setField("promotionCodeId", row.id === -1 ? null : row.id);
              }}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Feature</span>
            <DropDownList
              data={featureData}
              textField="label"
              dataItemKey="id"
              loading={features.isLoading}
              value={featureData.find((o) => o.id === (form.adFeatureId ?? -1)) ?? featureData[0]}
              onChange={(e) => {
                const row = e.target.value as NumericLookupRow;
                setField("adFeatureId", row.id === -1 ? null : row.id);
              }}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Feature Seq</span>
            <NumericTextBox value={form.adFeatureSequence ?? null} onChange={(e) => setField("adFeatureSequence", e.value ?? null)} />
          </label>
          <label className={s.field}>
            {/*
              Best History Ad (audit 21 MAJOR #4 item 10) — legacy txtBestHistAd
              is labelled "Best History Ad" but stores into BEST_HST_AD_DTE (a
              date column). The new SPA already renders this as the "Best
              History" DatePicker in the Ad & Promotion Dates card above; we
              keep a free-text mirror here so users who paste in YYYY-MM-DD
              from a legacy report don't have to hunt for it.
            */}
            <span className={s.fieldLabel}>Best History Ad (date)</span>
            <CleanDatePicker value={fromIso(form.bestHistoryDate)} onChange={(e) => setField("bestHistoryDate", toIso(e.value))} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Country of Origin</span>
            <Input value={form.countryOfOrigin ?? ""} onChange={(e) => setField("countryOfOrigin", String(e.target.value ?? "") || null)} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>3rd-Party Notification</span>
            <Input value={form.thirdPartyNotification ?? ""} onChange={(e) => setField("thirdPartyNotification", String(e.target.value ?? "") || null)} />
          </label>
          <label className={s.field}>
            {/*
              Floor Stock Protection (audit 21 MAJOR #4 item 15) — legacy
              ddlflrStkPtn is a tri-state DropDownList (blank / Yes / No).
              The wire column FSP_FG remains Y/N; this UI maps blank→false on
              save to match NewDeal.aspx.cs:771 behaviour.
            */}
            <span className={s.fieldLabel}>Floor Stock Protection</span>
            <DropDownList
              data={FSP_OPTIONS}
              textField="label"
              dataItemKey="id"
              value={FSP_OPTIONS.find((o) => o.id === fspChoice) ?? FSP_OPTIONS[0]}
              onChange={(e) => setFspChoice((e.target.value as { id: FspChoice }).id)}
            />
          </label>
        </section>

        <section className={s.card}>
          <h2 className={s.cardTitle}>Flags</h2>
          <div className={s.flagsGrid}>
            {([
              ["isDemo", "Demo"],
              ["isInternalDeal", "Internal Deal"],
              ["communityMarketExcluded", "Excl. Community Market"],
              ["hasOffInvoiceAllowance", "Off-Invoice Allowance"],
              ["hasBillBackAllowance", "Bill-Back Allowance"],
              ["hasCountRecountAllowance", "Count/Recount"],
              ["hasPassThruAllowance", "Pass-Thru"],
              ["hasScanbackAllowance", "Scanback"],
              ["allowanceReflectsToSixtyOne", "Reflect-to-61"],
              ["isRewardPromotion", "Reward Promotion"],
              ["isSecondaryPromotion", "Secondary Promotion"],
              // sendExpirationNotification (audit 21 MAJOR #4 item 14) — legacy
              // chk_expnotfn → DEAL.EXPTN_NOTFY_FG. Was already in the model
              // but the legacy label "Expiration Notification" is preserved.
              ["sendExpirationNotification", "Expiration Notification"],
              ["isGeac", "GEAC"],
              // isTermSheet (audit 21 MAJOR #4 item 16) — legacy chk_termSheet
              // → DEAL.TERM_SHEET_FG.
              ["isTermSheet", "Term Sheet"],
              ["suppressSavePriceFormat", "No-Save Price Format"],
              ["wineSaveUpTo", "Wine Sav-Up-To"],
              ["excludedFromAmp", "Excl. from AMP"],
            ] as ReadonlyArray<[keyof DealWriteRequest, string]>).map(([key, label]) => (
              <Checkbox
                key={key}
                label={label}
                checked={form[key] as boolean}
                onChange={(e) => {
                  const next = !!e.value;
                  // Audit 26 §3 item 3 — clear the paired allowance dates when
                  // the user un-ticks the gating flag, mirroring legacy
                  // NewDeal.aspx where the date controls live inside a panel
                  // tied to the checkbox's CheckedChanged handler. The flag
                  // itself updates in the same `setForm` call so React only
                  // schedules one re-render. Flags not listed here keep the
                  // simple single-field setField behaviour.
                  setForm((prev) => {
                    const patch: Partial<DealWriteRequest> = { [key]: next } as Partial<DealWriteRequest>;
                    if (!next) {
                      if (key === "hasScanbackAllowance") {
                        patch.scanbackStartDate = null;
                        patch.scanbackEndDate = null;
                      } else if (key === "hasCountRecountAllowance") {
                        patch.countRecountStartDate = null;
                        patch.countRecountEndDate = null;
                      } else if (key === "hasOffInvoiceAllowance") {
                        patch.firstOrderDate = null;
                        patch.lastOrderDate = null;
                      } else if (key === "hasBillBackAllowance") {
                        patch.firstShipmentDate = null;
                        patch.lastShipmentDate = null;
                      } else if (key === "isRewardPromotion") {
                        patch.rewardPromoStartDate = null;
                        patch.rewardPromoEndDate = null;
                      }
                    }
                    return { ...prev, ...patch };
                  });
                }}
              />
            ))}
          </div>
        </section>
      </div>

      <div className={s.sectionGrid}>
        <section className={s.card}>
          <h2 className={s.cardTitle}>Consumer Message</h2>
          <TextArea rows={3} value={form.consumerMessageComment ?? ""} onChange={(e) => setField("consumerMessageComment", String(e.value ?? "") || null)} />
        </section>
        <section className={s.card}>
          <h2 className={s.cardTitle}>Coupon Comment</h2>
          <TextArea rows={3} value={form.couponComment ?? ""} onChange={(e) => setField("couponComment", String(e.value ?? "") || null)} />
        </section>
        <section className={s.card}>
          <h2 className={s.cardTitle}>AMP — Store</h2>
          <TextArea rows={3} value={form.ampStoreComment ?? ""} onChange={(e) => setField("ampStoreComment", String(e.value ?? "") || null)} />
        </section>
        <section className={s.card}>
          <h2 className={s.cardTitle}>AMP — Merchandising</h2>
          <TextArea rows={3} value={form.ampMerchandisingComment ?? ""} onChange={(e) => setField("ampMerchandisingComment", String(e.value ?? "") || null)} />
        </section>
        <section className={s.card}>
          <h2 className={s.cardTitle}>AMP — Advertising</h2>
          <TextArea rows={3} value={form.ampAdvertisingComment ?? ""} onChange={(e) => setField("ampAdvertisingComment", String(e.value ?? "") || null)} />
        </section>
        <section className={s.card}>
          {/*
            Signage Text (audit 21 MAJOR #4 item 12) — legacy txtSignage backs
            DEAL.SIGNAGE_NOTES (no separate "signage text" column). Cardinality
            of one is fine; legacy "Signage Text" and the new model's
            `signageNotes` are the same Oracle column.
          */}
          <h2 className={s.cardTitle}>Signage Text</h2>
          <TextArea rows={3} maxLength={280} value={form.signageNotes ?? ""} onChange={(e) => setField("signageNotes", String(e.value ?? "") || null)} />
        </section>
      </div>

      <div className={s.actions}>
        <Button type="button" themeColor="base" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button type="submit" themeColor="primary" disabled={mutating.isPending}>
          {mutating.isPending ? "Saving…" : mode.kind === "create" ? "Create Deal" : "Save Changes"}
        </Button>
      </div>

      {mutating.isError && (
        <KError>Save failed — see banner above.</KError>
      )}
    </form>
  );
};

export default DealForm;
